import * as grow from "@theme/bp-grow-module";

const ROOT_SELECTOR = ".bp-pdp-grow";

/**
 * Formats an ISO date string (YYYY-MM-DD) as "Month Day" (e.g. "April 15").
 * @param {string} isoDate
 * @returns {string}
 */
const formatFrostDate = (isoDate) => {
  const [, month, day] = isoDate.split("-").map(Number);
  const date = new Date(2000, month - 1, day);

  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
};

/**
 * Adds `offsetDays` to an ISO date string and returns "Mon D".
 * @param {string} isoDate
 * @param {number} offsetDays
 * @returns {string}
 */
const offsetDate = (isoDate, offsetDays) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + offsetDays);

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/**
 * Returns "Mon D - Mon D" range string, or "—" if frost date is unavailable.
 * @param {string|null} frostDate
 * @param {number} minOffset
 * @param {number} maxOffset
 * @returns {string}
 */
const dateRange = (frostDate, minOffset, maxOffset) => {
  if (!frostDate) {
    // TODO: This is a temporary fallback. We'll need to design and create a separate state for this component for when BurpeePrefs are missing.
    return "—";
  }

  return `${offsetDate(frostDate, minOffset)} - ${offsetDate(frostDate, maxOffset)}`;
};

/**
 * Renders a schedule item row.
 * @param {string} heading
 * @param {{ label: string, range: string }[]} rows
 * @returns {HTMLDivElement}
 */
const createScheduleItem = (heading, rows) => {
  const item = document.createElement("div");
  item.className = "bp-pdp-grow__schedule-item";

  const h = document.createElement("strong");
  h.className = "bp-pdp-grow__schedule-heading";
  h.textContent = heading;
  item.appendChild(h);

  for (const { label, range } of rows) {
    const span = document.createElement("span");
    span.textContent = `${label}: ${range}`;
    item.appendChild(span);
  }

  return item;
};

const renderSchedule = (scheduleEl, calendar, lastFrost, firstFrost) => {
  scheduleEl.innerHTML = "";

  const hasIndoor = calendar.indoor_min != null && calendar.indoor_max != null;
  const hasFallIndoor =
    calendar.fall_indoor_min != null && calendar.fall_indoor_max != null;
  const hasTransplant =
    calendar.transplant_min != null && calendar.transplant_max != null;
  const hasFallTransplant =
    calendar.fall_transplant_min != null &&
    calendar.fall_transplant_max != null;
  const hasDirect = calendar.direct_min != null && calendar.direct_max != null;
  const hasFallDirect =
    calendar.fall_direct_min != null && calendar.fall_direct_max != null;

  if (hasIndoor || hasFallIndoor) {
    const rows = [];

    if (hasIndoor) {
      rows.push({
        label: "Start",
        range: dateRange(lastFrost, calendar.indoor_min, calendar.indoor_max),
      });
    }

    if (hasFallIndoor) {
      rows.push({
        label: "Fall Start",
        range: dateRange(
          firstFrost,
          calendar.fall_indoor_min,
          calendar.fall_indoor_max,
        ),
      });
    }

    if (hasTransplant) {
      rows.push({
        label: "Transplant",
        range: dateRange(
          lastFrost,
          calendar.transplant_min,
          calendar.transplant_max,
        ),
      });
    }

    if (hasFallTransplant) {
      rows.push({
        label: "Fall Transplant",
        range: dateRange(
          firstFrost,
          calendar.fall_transplant_min,
          calendar.fall_transplant_max,
        ),
      });
    }

    scheduleEl.appendChild(createScheduleItem("From Seed Indoors", rows));
  }

  if (hasTransplant || hasFallTransplant) {
    const rows = [];

    if (hasTransplant) {
      rows.push({
        label: "Start",
        range: dateRange(
          lastFrost,
          calendar.transplant_min,
          calendar.transplant_max,
        ),
      });
    }

    if (hasFallTransplant) {
      rows.push({
        label: "Fall Start",
        range: dateRange(
          firstFrost,
          calendar.fall_transplant_min,
          calendar.fall_transplant_max,
        ),
      });
    }

    scheduleEl.appendChild(createScheduleItem("From Plant", rows));
  }

  if (hasDirect || hasFallDirect) {
    const rows = [];

    if (hasDirect) {
      rows.push({
        label: "Start",
        range: dateRange(lastFrost, calendar.direct_min, calendar.direct_max),
      });
    }

    if (hasFallDirect) {
      rows.push({
        label: "Fall Start",
        range: dateRange(
          firstFrost,
          calendar.fall_direct_min,
          calendar.fall_direct_max,
        ),
      });
    }

    scheduleEl.appendChild(createScheduleItem("Direct Sow", rows));
  }
};

const handleRootClick = () => {
  grow.showCustomerZipCodeForm();
};

const render = () => {
  const root = document.querySelector(ROOT_SELECTOR);

  if (!root) {
    return;
  }

  const zipCode = grow.getZipCode();
  const state = grow.getState();
  const lastFrost = grow.getLastFrost();
  const firstFrost = grow.getFirstFrost();

  const zipEl = root.querySelector("[data-grow-zip]");
  const lastFrostEl = root.querySelector("[data-grow-last-frost]");
  const firstFrostEl = root.querySelector("[data-grow-first-frost]");
  const scheduleEl = root.querySelector("[data-grow-calendar]");
  const hasZip = Boolean(zipCode && state);

  root.classList.toggle("bp-pdp-grow--needs-zip", !hasZip);
  root.removeEventListener("click", handleRootClick);
  if (!hasZip) {
    root.addEventListener("click", handleRootClick);
  }

  if (zipEl) {
    if (hasZip) {
      zipEl.textContent = `${state} `;
      const link = zipEl.querySelector("a") || document.createElement("a");
      link.href = "#";
      link.className = "bp-pdp-link";
      link.textContent = zipCode;
      link.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        grow.showCustomerZipCodeForm();
      });
      zipEl.appendChild(link);
    } else {
      zipEl.innerHTML = "";
      const prompt = document.createElement("button");
      prompt.className = "bp-pdp-link";
      prompt.style.background = "none";
      prompt.style.border = "none";
      prompt.style.padding = "0";
      prompt.style.cursor = "pointer";
      prompt.textContent = "Enter your zip code";
      prompt.addEventListener("click", (e) => {
        e.stopPropagation();
        grow.showCustomerZipCodeForm();
      });
      zipEl.appendChild(prompt);
    }
  }

  if (lastFrostEl) {
    // TODO: Replace with approved design for missing prefs.
    lastFrostEl.textContent = lastFrost ? formatFrostDate(lastFrost) : "—";
  }

  if (firstFrostEl) {
    // TODO: Replace with approved design for missing prefs.
    firstFrostEl.textContent = firstFrost ? formatFrostDate(firstFrost) : "—";
  }

  if (scheduleEl) {
    const calendarData = scheduleEl.dataset.growCalendar;

    if (calendarData && calendarData !== "null") {
      const calendar = JSON.parse(calendarData);
      renderSchedule(scheduleEl, calendar, lastFrost, firstFrost);
    }
  }
};

render();

window.addEventListener("onLocationChange", render);
